import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDossier } from './queries/useDossiers';
import { useAuthStore } from '../store/useAuthStore';
import { getRouteLabel } from '../lib/routeLabels';
import {
  joinPresenceCabinet,
  type PresencePayload,
} from '../services/messaging/presenceService';

const DOSSIER_PATH_RE = /^\/dossiers\/([0-9a-fA-F-]{36})/;

function getDossierIdFromPath(pathname: string): string | null {
  const match = pathname.match(DOSSIER_PATH_RE);
  return match ? match[1]! : null;
}

function buildPayload(
  collaborator: { id: string; full_name: string; avatar_url: string | null },
  pathname: string,
  dossierTitle: string | null,
): PresencePayload {
  const dossierId = getDossierIdFromPath(pathname);
  if (dossierId) {
    return {
      collaborator_id: collaborator.id,
      full_name: collaborator.full_name,
      avatar_url: collaborator.avatar_url,
      context_type: 'dossier',
      context_id: dossierId,
      context_label: dossierTitle ? `Dossier ${dossierTitle}` : 'Dossier',
      online_at: new Date().toISOString(),
    };
  }
  return {
    collaborator_id: collaborator.id,
    full_name: collaborator.full_name,
    avatar_url: collaborator.avatar_url,
    context_type: 'route',
    context_id: null,
    context_label: getRouteLabel(pathname),
    online_at: new Date().toISOString(),
  };
}

/**
 * Monte le canal Presence et diffuse le contexte courant à chaque navigation.
 * À monter UNE seule fois dans MainLayout via <PresenceBroadcast />.
 *
 * useDossier(dossierId) est observé : quand on arrive sur /dossiers/:id, on
 * broadcaste d'abord "Dossier" (fallback), puis "Dossier {title}" dès que
 * la query fetch et résout. Sans ce hook, le titre du dossier ne serait
 * disponible que si l'utilisateur avait déjà visité la fiche.
 */
export function usePresenceBroadcast(): void {
  const collaborator = useAuthStore((s) => s.collaborator);
  const location = useLocation();
  const handleRef = useRef<ReturnType<typeof joinPresenceCabinet> | null>(null);

  const dossierId = getDossierIdFromPath(location.pathname);
  const { data: dossier } = useDossier(dossierId ?? undefined);
  const dossierTitle = dossier?.title ?? null;

  // Rejoindre le canal au montage (et à chaque login/logout)
  useEffect(() => {
    if (!collaborator) return;
    const initial = buildPayload(
      { id: collaborator.id, full_name: collaborator.full_name ?? '', avatar_url: collaborator.avatar_url },
      location.pathname,
      dossierTitle,
    );
    const handle = joinPresenceCabinet(initial);
    handleRef.current = handle;
    return () => {
      handle.leave();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on ne veut re-join qu'au changement de collaborateur
  }, [collaborator?.id]);

  // Re-track à chaque changement de pathname OU de titre dossier
  useEffect(() => {
    if (!collaborator || !handleRef.current) return;
    const next = buildPayload(
      { id: collaborator.id, full_name: collaborator.full_name ?? '', avatar_url: collaborator.avatar_url },
      location.pathname,
      dossierTitle,
    );
    void handleRef.current.updatePayload(next);
  }, [collaborator, location.pathname, dossierTitle]);
}
